import {
  Chip,
  DatePicker,
  DatePickerLocales,
  DropdownMenu,
  Icon,
} from '@toggle/design-system';
import { formatDateToLocale, Formats } from '@toggle/helpers';
import { isDate, isToday, isYesterday, startOfYesterday } from 'date-fns';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Option } from '~/components/filter/Option';
import { useLanguageStore } from '~/stores/use-language-store/useLanguageStore';

import * as S from './DatePillDropdown.styles';

export interface DatePillDropdownAction {
  date: string;
}

export interface DatePillDropdownProps {
  originalPillTime: string;
  onChangeDate: (action: DatePillDropdownAction) => void;
  minDate?: Date;
  className?: string;
  dateFormat?: keyof typeof Formats;
}

export const DatePillDropdown = (({
  originalPillTime,
  onChangeDate,
  minDate,
  className,
  dateFormat = 'EEEE, dd MMMM',
}: DatePillDropdownProps) => {
  const { t } = useTranslation(['news', 'datePicker']);

  const datePickerLocales: DatePickerLocales = {
    selectYear: t('datePicker:select_year'),
    selectMonth: t('datePicker:select_month'),
    selectDecade: t('datePicker:select_decade'),
    previousMonth: t('datePicker:previous_month'),
    previousYear: t('datePicker:previous_year'),
    previousDecade: t('datePicker:previous_decade'),
    nextMonth: t('datePicker:next_month'),
    nextYear: t('datePicker:next_year'),
    nextDecade: t('datePicker:next_decade'),
  };

  const [isShowDatePicker, setIsShowDatePicker] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const languageSetting = useLanguageStore(state => state.languageSetting);

  const ref = useRef<HTMLButtonElement>(null);

  const pillLabel = () => {
    const labelTime = new Date(originalPillTime);

    if (isToday(labelTime)) {
      return t('news:pillDropdown.today');
    } else if (isYesterday(labelTime)) {
      return t('news:pillDropdown.yesterday');
    } else {
      return formatDateToLocale(
        languageSetting,
        labelTime,
        Formats[dateFormat]
      );
    }
  };

  const pillOptions = [
    {
      key: 'today',
      name: t('news:pillDropdown.today'),
      value: { value: new Date() },
      isChecked: originalPillTime ? isToday(new Date(originalPillTime)) : false,
    },
    {
      key: 'yesterday',
      name: t('news:pillDropdown.yesterday'),
      value: { value: startOfYesterday() },
      isChecked: originalPillTime
        ? isYesterday(new Date(originalPillTime))
        : false,
    },
  ];

  return (
    <div className={className}>
      <DropdownMenu
        open={isDropdownOpen}
        onOpenChange={() => {
          setIsShowDatePicker(false);
          setIsDropdownOpen(prev => !prev);
        }}
        triggerNode={
          <S.ChipWrapper>
            <Chip
              ref={ref}
              label={pillLabel()}
              rightIcon="ChevronLightDown"
              rightIconSize={12}
              data-testid="date-pill-chip"
            />
          </S.ChipWrapper>
        }
        contentProps={{
          asChild: true,
        }}
      >
        <S.MenuWrapper $isCalendarShow={isShowDatePicker}>
          {isShowDatePicker ? (
            <DatePicker
              value={new Date(originalPillTime)}
              maxDate={new Date()}
              minDate={minDate}
              onDateChange={newDate => {
                if (newDate && isDate(newDate)) {
                  onChangeDate({
                    date: (newDate as Date).toISOString(),
                  });
                  setIsDropdownOpen(false);
                }
              }}
              locales={datePickerLocales}
              data-testid="calendar"
            />
          ) : (
            <>
              {pillOptions.map(option => {
                return (
                  <Option
                    key={option.key}
                    option={option}
                    handleClick={() => {
                      onChangeDate({
                        date: option.value.value.toISOString(),
                      });
                    }}
                    isChecked={option.isChecked}
                    data-testid={`time-${option.key}`}
                  />
                );
              })}
              <S.Divider />
              <S.CustomDate
                onClick={() => setIsShowDatePicker(true)}
                data-testid="date-picker-opener"
              >
                <Icon iconName="Settings" size={16} />
                <S.SpecificDate>
                  {t('news:pillDropdown.specificDate')}
                </S.SpecificDate>
              </S.CustomDate>
            </>
          )}
        </S.MenuWrapper>
      </DropdownMenu>
    </div>
  );
}) as React.FC<DatePillDropdownProps> & { Styled: typeof S };

DatePillDropdown.Styled = S;
